import React from 'react';
import { useTranslation } from 'react-i18next';
import { useAtomValue } from 'jotai';
import { selectedConversationIdAtom } from '../../../../atoms/v3/chatAtoms';

interface ChatItem {
  id: string;
  name: string;
  lastMessage: string;
  time: string;
  unread: number;
  avatar?: string;
}

interface ChatListProps {
  chats: ChatItem[];
  onChatSelect: (chatId: string) => void;
}

/**
 * iOS 风格的聊天列表组件
 */
export const ChatList: React.FC<ChatListProps> = ({ chats, onChatSelect }) => {
  const { t } = useTranslation(['v3/chat', 'common']);
  const selectedConversationId = useAtomValue(selectedConversationIdAtom);

  return (
    <div className="divide-y divide-ios-gray5 dark:divide-ios-dark-gray5">
      {chats.map(chat => {
        const isCurrentChat = chat.id === selectedConversationId;

        return (
          <div
            key={chat.id}
            className="py-3 px-4 flex items-center animate-ios-fade-in cursor-pointer"
            onClick={() => onChatSelect(chat.id)}
          >
            {/* 头像 */}
            <div className="relative">
              <div className="w-12 h-12 rounded-full bg-ios-blue dark:bg-ios-dark-blue flex items-center justify-center text-white text-lg font-semibold overflow-hidden">
                {chat.avatar ? (
                  <img src={chat.avatar} alt={chat.name} className="w-full h-full object-cover" />
                ) : (
                  chat.name.charAt(0)
                )}
              </div>

              {/* 未读消息标记 */}
              {chat.unread > 0 && (
                <div className="absolute -top-1 -right-1 w-5 h-5 bg-ios-red dark:bg-ios-dark-red rounded-full flex items-center justify-center text-white text-xs font-bold">
                  {chat.unread}
                </div>
              )}
            </div>

            {/* 聊天信息 */}
            <div className="ml-3 flex-1 min-w-0">
              <div className="flex justify-between items-baseline">
                <div className="flex items-center">
                  <h3 className="text-ios-body font-semibold truncate">{chat.name}</h3>

                  {/* 当前对话标签 */}
                  {isCurrentChat && (
                    <span className="ml-2 px-1.5 py-0.5 text-xs font-medium bg-ios-blue/10 dark:bg-ios-dark-blue/20 text-ios-blue dark:text-ios-dark-blue rounded">
                      {t('v3/chat:labels.currentChat', '当前对话')}
                    </span>
                  )}
                </div>
                <span className="text-ios-footnote text-ios-gray dark:text-ios-dark-gray ml-2 flex-shrink-0">{chat.time}</span>
              </div>
              <p className={`text-ios-subhead truncate ${
                chat.unread > 0
                  ? 'text-ios-gray dark:text-ios-dark-gray font-medium'
                  : 'text-ios-gray2 dark:text-ios-dark-gray2'
              }`}>
                {chat.lastMessage}
              </p>
            </div>
          </div>
        );
      })}
    </div>
  );
};
